<template>
  <div class="flex flex-grow flex-col md:mx-8">
    <div
      class="sticky top-10 z-10 flex w-full justify-center bg-bg-header pb-3 pt-3 md:hidden">
      <DatesSelection
        class="gc-font-small-title pb-0 pt-0"
        :startDate="query.startDate"
        :endDate="query.endDate" />
      <i class="ml-2 h-4 w-4" @click="goHome">
        <component :is="BorderColor" />
      </i>
    </div>
    <div class="relative flex">
      <div
        class="fill-height sticky top-24 -mt-1 hidden min-w-min basis-1/4 flex-col overflow-y-auto pr-6 md:flex">
        <div class="mt-4 flex justify-center">
          <DatesSelection
            class="gc-font-small-title pt-2 md:!text-dark"
            :startDate="state.datesSelected.startDate"
            :endDate="state.datesSelected.endDate" />
        </div>
        <Calendar
          v-if="!isMobile"
          :initialStartDate="query.startDate"
          :initialEndDate="query.endDate"
          :maxDate="applicationSetup.end_date"
          :showBar="applicationSetup.show_bar"
          :currency="applicationSetup.currency"
          :helperCalendar="true"
          @datesSelected="datesSelected"
          @setDatesSelected="setDatesSelected" />

        <WarningInfo
          :class="'hidden justify-center pb-4 md:flex'"
          :textClass="'px-1 py-0.5'"
          :text="
            $t(
              applicationSetup?.show_taxes_and_fees
                ? 'general.calendarRatesInfoNoteDetailed'
                : 'general.calendarRatesInfoNote',
              { symbol: applicationSetup.currencySymbol },
            )
          " />
        <div class="mx-0.5 flex flex-col">
          <QuerySelectButton
            v-for="(button, index) in querySelectButtonOption"
            :key="index"
            :title="button.title"
            :body="button.body"
            :component="button.component"
            @click="button.onClick"
            :aria-label="button.ariaLabel" />
          <CustomDrawer
            v-model="drawerOpen"
            :position="isMobile ? 'bottom' : 'right'"
            width="w-[35%]"
            height="h-auto"
            @update:modelValue="onDrawerClose">
            <template #drawerContent>
              <component
                :is="currentComponent"
                @close="drawerOpen = false"></component>
            </template>
          </CustomDrawer>
          <CustomDrawer
            v-model="policiesVisible"
            :position="isMobile ? 'bottom' : 'right'"
            :width="isMobile ? 'w-full' : 'w-[35%]'"
            height="h-auto">
            <template #drawerContent>
              <OfferPolicies
                :roomRateId="policiesRoomRateId"
                @close="closePolicies" />
            </template>
          </CustomDrawer>
          <CustomDrawer
            v-model="rezlockDrawerVisible"
            :position="isMobile ? 'bottom' : 'right'"
            :width="isMobile ? 'w-full' : 'w-[35%]'"
            height="h-auto">
            <template #drawerContent>
              <RezlockDetails
                :prices="rezLockValues"
                @selectRezlock="selectRezlock" />
            </template>
          </CustomDrawer>
        </div>
      </div>
      <div
        :class="[
          'relative mt-4 flex w-full basis-full flex-col md:basis-3/4 lg:ml-6',
          isMultiRoom ? 'md:mt-11' : '',
        ]">
        <MultipleRoomsList v-if="isMultiRoom" />
        <SearchList
          v-if="props.section == 'list'"
          @select="onRateOrRoomSelect" />
        <RoomDetails
          v-if="props.section == 'roomDetails'"
          @select="onRateOrRoomSelect" />
        <RatePlanDetails
          v-if="props.section == 'rateDetails'"
          @select="onRateOrRoomSelect" />
        <div
          v-if="
            (props.section == 'rateDetails' ||
              props.section == 'roomDetails') &&
            isFirstLoad
          "
          class="text-center text-brand">
          <h2
            class="gc-font-notes !font-secondary mb-8 mt-4 inline-block cursor-pointer rounded-gc-sm px-6 py-2 text-dark underline outline outline-1 outline-dark transition-all duration-300"
            @click="goToSearch">
            {{ $t('general.showAllOffers') }}
          </h2>
        </div>
        <IconButton
          :buttonClass="`h-8 w-8 fixed flex items-center justify-center border-0 !text-dark hover:!bg-light m-2.5 focus:!bg-light focus:text-light ${isMultiRoom ? 'mt-12 md:mt-2.5' : ''}`"
          ariaLabel="left"
          :icon="ChevronLeftIcon"
          iconClass="h-4 w-4 stroke-2"
          @click="goBack" />
      </div>
    </div>
    <CustomDrawer
      v-model="multiRoomDrawerVisible"
      :position="isMobile ? 'bottom' : 'right'"
      :width="isMobile ? 'w-full' : 'w-[35%]'"
      height="h-auto"
      drawerContentClass="flex flex-col flex-grow max-h-full mx-10p">
      <template #drawerContent>
        <MultiRoomSelection @hideMultiRoomDrawer="hideMultiRoomDrawer" />
      </template>
    </CustomDrawer>
  </div>
</template>
<script setup>
import {
  ref,
  computed,
  shallowRef,
  defineAsyncComponent,
  provide,
  inject,
  watch,
  onMounted,
  onUnmounted,
} from 'vue';
import { i18n } from '@/locales/i18n';
import { storeToRefs } from 'pinia';
import router from '@/router';
import { useRoute } from 'vue-router';
import IconButton from '@/components/customComponents/IconButton.vue';

const SearchList = defineAsyncComponent(
  () => import('@/components/Results/SearchList.vue'),
);
const RoomDetails = defineAsyncComponent(
  () => import('@/components/Results/RoomDetails.vue'),
);
const RatePlanDetails = defineAsyncComponent(
  () => import('@/components/Results/RatePlanDetails.vue'),
);
const OfferPolicies = defineAsyncComponent(
  () => import('@/components/Results/OfferPolicies.vue'),
);
const DatesSelection = defineAsyncComponent(
  () => import('@/components/Calendar/DatesSelection.vue'),
);
const Calendar = defineAsyncComponent(
  () => import('@/components/CalendarCore.vue'),
);
const OccupancyRoom = defineAsyncComponent(
  () => import('@/components/Occupancy/OccupancyRoom.vue'),
);
const QuerySelectButton = defineAsyncComponent(
  () => import('@/components/QuerySelectButton.vue'),
);
const SpecialRates = defineAsyncComponent(
  () => import('@/components/SpecialRates/SpecialRates.vue'),
);
const CustomDrawer = defineAsyncComponent(
  () => import('@/components/customComponents/CustomDrawer.vue'),
);
const RezlockDetails = defineAsyncComponent(
  () => import('@/components/Checkout/RezlockDetails.vue'),
);
const PromotionChipList = defineAsyncComponent(
  () => import('@/components/customComponents/PromotionChipList.vue'),
);

import WarningInfo from '@/components/Calendar/WarningInfo.vue';

import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useScreenSize } from '@composables/useScreenSize';
import BorderColor from '@icons/border_color.svg';
import ChevronLeftIcon from '@icons/chevron-left.svg';

import { useOffersStore } from '@stores/offers.store';
import { useReservationStore } from '@/stores/reservation.store';
import MultipleRoomsList from '@/components/Results/MultipleRoomsList.vue';
import MultiRoomSelection from '@/components/Checkout/MultiRoomSelection.vue';
const reservationStore = useReservationStore();
const { resetReservation } = reservationStore;
const offersStore = useOffersStore();
const { getRoomInfo, getRateInfo, getRoomRateInfo, checkCurrentRoomSelected } =
  offersStore;
const { isMultiRoom } = storeToRefs(offersStore);

provide('isMultiRoom', isMultiRoom);

import { useAddonsStore } from '@stores/addons.store';
const addonStore = useAddonsStore();
const { getAllAddons } = addonStore;

const route = useRoute();

const props = defineProps({
  section: String,
});

const bookingQueryStore = useBookingQueryStore();

const applicationSetup = inject('applicationSetup');
const { query, userOccupancyTotals } = storeToRefs(bookingQueryStore);
const { setStartDate, setEndDate } = bookingQueryStore;
const { isMobile } = useScreenSize();

const goBack = inject('goBack');

const drawerOpen = ref(false);
const currentComponent = shallowRef(null);
const currentComponentName = ref('');

const isFirstLoad = ref(true);

onMounted(() => {
  window.scrollTo(0, 0);
});

onUnmounted(() => {
  isFirstLoad.value = false;
});

const state = ref({
  datesSelected: {
    startDate: query.value.startDate,
    endDate: query.value.endDate,
  },
});

const policiesRoomRateId = ref('');
const policiesVisible = ref(false);

const viewPolicies = (roomRateId) => {
  policiesVisible.value = true;
  policiesRoomRateId.value = roomRateId;
};

const closePolicies = () => {
  policiesVisible.value = false;
};

provide('viewPolicies', viewPolicies);

const rezlockRoomRateCode = ref('');
const rezlockDrawerVisible = ref(false);
const viewRezlockDrawer = (roomRateId) => {
  rezlockDrawerVisible.value = true;
  rezlockRoomRateCode.value = roomRateId;
};

const rezLockValues = computed(() => {
  let roomId = rezlockRoomRateCode.value.split('.')[1];
  let rateId = rezlockRoomRateCode.value.split('.')[0];
  const rate = getRoomRateInfo(rateId, roomId);
  return rate.rezlock_values ?? [];
});

const closeRezlockDrawer = () => {
  rezlockDrawerVisible.value = false;
};

provide('viewRezlock', viewRezlockDrawer);

const selectRezlock = (rezlock) => {
  let roomId = rezlockRoomRateCode.value.split('.')[1];
  let rateId = rezlockRoomRateCode.value.split('.')[0];
  closeRezlockDrawer();
  const roomResult = getRoomInfo(roomId);
  const rateInfo = getRateInfo(rateId);
  const rate = getRoomRateInfo(rateId, roomId);

  const selectedMealPlan = rate.meal_plan_prices.included.id;
  const rezlockPrice = rate.rezlock_values[rezlock];
  const params = {
    room_rate_code: rezlockRoomRateCode.value,
    mealPlan: selectedMealPlan,
    room: roomResult,
    rate: rateInfo,
    roomRate: rate,
    rezlock: true,
    rezlockTime: rezlock,
    rezlockPrice: rezlockPrice,
  };

  reservationStore.addReservation(params);
  router.push({
    name: 'guest details',
    query: route.query,
  });
};

const goHome = () => {
  router.push({ name: 'home', query: route.query });
};

const goToSearch = () => {
  router.push({ name: 'search', query: route.query }).then(() => {
    window.scrollTo(0, 0);
  });
};

const shouldWatchRoute = computed(() => {
  return props.section === 'rateDetails' || props.section === 'roomDetails';
});

watch(
  () => route.name,
  (newRoute) => {
    if (newRoute !== 'search') {
      isFirstLoad.value = false;
    }
  },
  { immediate: false, enabled: shouldWatchRoute },
);

const datesSelected = (dates) => {
  state.value.datesSelected.startDate = dates[0];
  state.value.datesSelected.endDate = dates[1];
};

const setDatesSelected = () => {
  setStartDate(state.value.datesSelected.startDate);
  setEndDate(state.value.datesSelected.endDate);

  resetReservation();
};

const querySelectButtonOption = computed(() => [
  {
    title: i18n.global.t('general.occupancy.title'),
    body:
      i18n.global.t('general.roomsTitle', {
        n: userOccupancyTotals.value.totalRooms,
      }) +
      ', ' +
      i18n.global.t('general.occupancy.adults', {
        n: userOccupancyTotals.value.totalAdults,
      }) +
      ' ' +
      (userOccupancyTotals.value.totalChildren > 0
        ? ' ' +
          i18n.global.t('general.occupancy.children', {
            n: userOccupancyTotals.value.totalChildren,
          })
        : '') +
      (userOccupancyTotals.value.totalBabies > 0
        ? ' ' +
          i18n.global.t('general.occupancy.babies', {
            n: userOccupancyTotals.value.totalBabies,
          })
        : ''),
    onClick: () => querySelectButtonAction('Occupancy', OccupancyRoom),
    ariaLabel: 'Occupancy',
  },
  {
    title: i18n.global.t('promos'),
    component: PromotionChipList,
    onClick: () => querySelectButtonAction('Promos', SpecialRates),
    ariaLabel: i18n.global.t('promosAndSpecialRates'),
  },
]);

watch(
  () => isMobile.value,
  (value) => {
    if (value) {
      drawerOpen.value = false;
    }
  },
);

const querySelectButtonAction = (id, component) => {
  currentComponentName.value = id;
  drawerOpen.value = !drawerOpen.value;
  currentComponent.value = component;
};

const multiRoomDrawerVisible = ref(false);
const hideMultiRoomDrawer = () => {
  multiRoomDrawerVisible.value = false;
};

const onRateOrRoomSelect = async (roomRateCode, result) => {
  const addons = await getAllAddons(roomRateCode.split('.')[1]);

  const offer = result.offers.find(
    (offer) => offer.room_rate_code === roomRateCode,
  );

  if (offer.meal_plan_prices.upgrades.length === 0 && addons.length === 0) {
    const params = getReservationParameters(roomRateCode, result);
    const { redirect, showDrawer } = reservationStore.addReservation(params);
    if (showDrawer) {
      multiRoomDrawerVisible.value = true;
      return;
    }
    if (redirect) {
      router
        .push({
          name: redirect,
          query: route.query,
        })
        .then(() => {
          checkCurrentRoomSelected();
          window.scrollTo(0, 0);
        });
    }
  } else {
    router.push({
      name: 'extras',
      params: { id: roomRateCode },
      query: route.query,
    });
  }
};

const getReservationParameters = (roomRateCode, result) => {
  const offer = result.offers.find(
    (offer) => offer.room_rate_code === roomRateCode,
  );

  const [rateId, roomId] = roomRateCode.split('.');
  const roomResult = getRoomInfo(roomId);
  const rateInfo = getRateInfo(rateId);
  const rate = getRoomRateInfo(rateId, roomId);

  return {
    room_rate_code: roomRateCode,
    mealPlan: offer.meal_plan.id,
    room: roomResult,
    rate: rateInfo,
    roomRate: rate,
  };
};

const onDrawerClose = () => {
  if (currentComponentName.value === 'Promos') {
    const invalidChip = document.querySelector('.invalid-chip');
    const chipInputElement = document.querySelector('.chip-input');
    const invalidChipValue = chipInputElement ? chipInputElement.value : '';
    if (invalidChip || invalidChipValue.length > 0) {
      drawerOpen.value = true;
    }
  }
};
</script>

<style scoped>
.fill-height {
  height: calc(100vh - 96px - 38px);
}
</style>
